import React, { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import './App.css';
import CropSelector from './components/CropSelector';
import FileUpload from './components/FileUpload';
import PhenotypeSearch from './components/PhenotypeSearch';
import AIBot from './components/AIBot';
import AddCultivars from './components/AddCultivars';
import RenameCultivar from './components/RenameCultivar';
import SimilarityIndex from './components/SimilarityIndex';
import GenomicAnalysisContainer from './components/GenomicAnalysisContainer';
import SNPTools from './components/SNPTools';
import LogoutButton from './components/LogoutButton';
import UserProfile from './components/UserProfile';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';

const CROP_LABELS = {
  barley: { emoji: '🌾', name: 'Barley' },
  wheat: { emoji: '🌿', name: 'Wheat' },
  potato: { emoji: '🥔', name: 'Potato' },
  oats: { emoji: '🌱', name: 'Oats' },
  strawberry: { emoji: '🍓', name: 'Strawberry' },
  forages: { emoji: '🌿', name: 'Forages' },
  other: { emoji: '🧬', name: 'Other' },
};

/* ── Navigation structure with logical groups ── */
const NAV_GROUPS = [
  {
    label: 'Data',
    items: [
      { key: 'upload',    icon: '📤', label: 'Upload' },
      { key: 'explorer',  icon: '📊', label: 'Data Explorer' },
      { key: 'cultivars', icon: '🌱', label: 'Add Cultivars' },
      { key: 'rename',    icon: '🏷️', label: 'Rename' },
    ],
  },
  {
    label: 'Analysis',
    items: [
      { key: 'snptools',   icon: '🔧', label: 'SNP Tools' },
      { key: 'similarity',  icon: '🔍', label: 'Similarity' },
      { key: 'genomic',    icon: '🧬', label: 'Genomic Analysis' },
    ],
  },
  {
    label: 'AI',
    items: [
      { key: 'aibot', icon: '🤖', label: 'AI Assistant' },
    ],
  },
];

function App() {
  const { isAuthenticated, isLoading } = useAuth0();
  const [selectedCrop, setSelectedCrop] = useState(null);
  const [activeView, setActiveView] = useState('explorer');
  const [refreshKey, setRefreshKey] = useState(0);

  const handleUploadSuccess = () => {
    setRefreshKey(prev => prev + 1);
  };

  const handleSelectCrop = (crop) => {
    setSelectedCrop(crop);
    setActiveView('explorer');
    setRefreshKey(prev => prev + 1);
  };

  const handleBackToCrops = () => {
    setSelectedCrop(null);
    setActiveView('explorer');
  };

  if (isLoading) {
    return (
      <div className="App">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login />;
  }

  if (!selectedCrop) {
    return (
      <div className="App">
        <ProtectedRoute>
          <CropSelector onSelectCrop={handleSelectCrop} />
        </ProtectedRoute>
      </div>
    );
  }

  const cropInfo = CROP_LABELS[selectedCrop] || CROP_LABELS.other;

  return (
    <div className="App">
      <header className="header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button
              onClick={handleBackToCrops}
              className="back-to-crops-btn"
              title="Back to crop selection"
            >
              ← Crops
            </button>
            <div className="crop-breadcrumb">
              <span className="crop-breadcrumb-icon">{cropInfo.emoji}</span>
              <span className="crop-breadcrumb-name">{cropInfo.name}</span>
            </div>
            <h1 style={{ margin: 0 }}>Genomic Database Platform</h1>
          </div>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            <UserProfile />
            <LogoutButton />
          </div>
        </div>
      </header>

      {/* ── Navigation Bar ── */}
      <nav className="nav">
        {NAV_GROUPS.map((group, gi) => (
          <React.Fragment key={group.label}>
            {gi > 0 && <span className="nav-divider" />}
            {group.items.map(item => (
              <button
                key={item.key}
                className={activeView === item.key ? 'active' : ''}
                onClick={() => setActiveView(item.key)}
              >
                {item.icon} {item.label}
              </button>
            ))}
          </React.Fragment>
        ))}
      </nav>

      <div className="container">
        {activeView === 'upload' && (
          <ProtectedRoute>
            <FileUpload onUploadSuccess={handleUploadSuccess} crop={selectedCrop} />
          </ProtectedRoute>
        )}

        {activeView === 'explorer' && (
          <ProtectedRoute>
            <PhenotypeSearch key={`${selectedCrop}-${refreshKey}`} crop={selectedCrop} />
          </ProtectedRoute>
        )}

        {activeView === 'cultivars' && (
          <ProtectedRoute>
            <AddCultivars crop={selectedCrop} />
          </ProtectedRoute>
        )}

        {activeView === 'rename' && (
          <ProtectedRoute>
            <RenameCultivar crop={selectedCrop} />
          </ProtectedRoute>
        )}

        {activeView === 'snptools' && (
          <ProtectedRoute>
            <SNPTools key={`${selectedCrop}-${refreshKey}`} crop={selectedCrop} />
          </ProtectedRoute>
        )}

        {activeView === 'similarity' && (
          <ProtectedRoute>
            <SimilarityIndex key={`${selectedCrop}-${refreshKey}`} crop={selectedCrop} />
          </ProtectedRoute>
        )}

        {activeView === 'genomic' && (
          <ProtectedRoute>
            <GenomicAnalysisContainer key={`${selectedCrop}-${refreshKey}`} crop={selectedCrop} />
          </ProtectedRoute>
        )}

        {activeView === 'aibot' && (
          <ProtectedRoute requiredRole="admin">
            <AIBot key={`${selectedCrop}-${refreshKey}`} crop={selectedCrop} />
          </ProtectedRoute>
        )}
      </div>

      {/* ── Footer ── */}
      <footer style={{
        textAlign: 'center',
        padding: '20px 24px',
        color: '#999',
        fontSize: '13px',
        borderTop: '1px solid #e6e6e6',
        marginTop: '40px',
        background: 'rgba(255,255,255,0.5)'
      }}>
        © Kovi MR — Graminor &nbsp;·&nbsp; Genomic Database Platform v2.0
      </footer>
    </div>
  );
}

export default App;
